import type { ServiceLabels } from '@/lib/form-state/util'

/**
 * Rótulos das categorias de lançamento (`Billing.category`).
 *
 * A categoria é gravada pelo backend como chave em inglês (é ela que os relatórios e
 * a geração de repasse filtram) e não tem enum no schema — por isso o mapa vive aqui,
 * e não como tradução automática: cada chave nova precisa de um rótulo escrito à mão.
 *
 * Chave sem rótulo cai no próprio valor cru, que é melhor que "—": pelo menos o
 * operador consegue relatar o que apareceu.
 */
export const BILLING_CATEGORY_LABELS: Record<string, string> = {
    // Repasses gerados automaticamente quando um recebimento é pago
    PARTNER_PAYOUT: 'Repasse a parceiro',
    PROFESSIONAL_PAYOUT: 'Repasse a profissional',
    HOSPITAL_PAYOUT: 'Repasse a hospital',
    SUPPLIER_PAYOUT: 'Repasse a fornecedor',
    /** Solicitação agrupada: soma os repasses de um destinatário num período. */
    PAYOUT_BATCH: 'Repasse agrupado',

    // Origem do lançamento
    CONSULTATION: 'Consulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
    UTILITY: 'Despesa operacional',

    // Assinatura e revenda
    SUBSCRIPTION: 'Assinatura',
    RESALE_SUBSCRIPTION: 'Assinatura de revenda',
    RESALE_LEDGER: 'Repasse de revenda',

    /** Cobrança encontrada no provedor sem correspondente aqui (conciliação). */
    ASAAS_ORPHAN: 'Cobrança órfã do provedor',
}

/**
 * Rótulo da categoria. `labels` traduz as chaves de atendimento para o segmento
 * da empresa (fora da saúde, Procedimento vira Pedido) — passe
 * `useServiceLabel()` sempre que o texto for exibido ao operador.
 */
export function billingCategoryLabel(
    category?: string | null,
    labels?: ServiceLabels
): string {
    if (!category) return ''
    if (labels) {
        const translated: Record<string, string> = {
            CONSULTATION: labels.Consultation,
            EXAM: labels.Exam,
            PROCEDURE: labels.Order,
        }
        if (translated[category]) return translated[category]
    }
    return BILLING_CATEGORY_LABELS[category] ?? category
}

/**
 * Categorias que o SISTEMA grava, e que o operador não escolhe nem edita.
 *
 * O campo "Categoria" do formulário é texto livre ("Aluguel", "Material") — serve para
 * a conta lançada à mão. Já um repasse nasce com um destes códigos, e é por ele que o
 * agrupamento encontra o que agrupar (`findBatchablePayouts` filtra por
 * `category IN (...)`). Trocado por "Repasse do Dr. Fulano", o lançamento some do modal
 * de geração e nunca é pago, sem erro nenhum.
 *
 * Só os repasses entram aqui. `CONSULTATION`, `SUBSCRIPTION` e afins também são
 * gravados pelo sistema, mas ninguém filtra por eles para mover dinheiro — editá-los
 * atrapalha um relatório, não um pagamento.
 */
const SYSTEM_CATEGORIES = new Set([
    'PARTNER_PAYOUT',
    'PROFESSIONAL_PAYOUT',
    'HOSPITAL_PAYOUT',
    'SUPPLIER_PAYOUT',
    'PAYOUT_BATCH',
])

export function isBillingSystemCategory(category?: string | null): boolean {
    return !!category && SYSTEM_CATEGORIES.has(category)
}

/**
 * As quatro categorias de repasse — as únicas que o agrupamento enxerga
 * (`PAYOUT_CATEGORIES` no backend, filtradas em `buildBatchablePayoutWhere`).
 */
export function isPayoutCategory(category?: string | null): boolean {
    return (
        !!category &&
        ['PARTNER_PAYOUT', 'PROFESSIONAL_PAYOUT', 'HOSPITAL_PAYOUT', 'SUPPLIER_PAYOUT'].includes(
            category
        )
    )
}

/**
 * Categoria que o operador pode escolher numa conta a pagar lançada à mão.
 *
 * `recipientTypes` diz para quais destinatários a opção faz sentido: um repasse de
 * parceiro só existe com parceiro preenchido, porque o agrupamento lê `partnerId` a
 * partir da categoria (`PAYOUT_CATEGORY_TO_KIND` no backend). "Repasse a parceiro"
 * gravado num lançamento cujo destinatário é um fornecedor não seria agrupável em
 * nome de ninguém — o lançamento sumiria do modal como "sem destinatário preenchido".
 *
 * `undefined` vale para qualquer destinatário.
 */
export interface ManualPayableCategoryOption {
    value: string
    label: string
    recipientTypes?: string[]
    /** Explica o efeito da escolha, exibido abaixo do select. */
    hint?: string
}

/**
 * O que o select "Categoria" oferece numa conta a pagar manual.
 *
 * Existe porque o campo era texto livre, e repasse digitado à mão ("Repasse a
 * credenciado") nascia fora do agrupamento sem erro nenhum: o modal de geração
 * descartava o lançamento em silêncio e o parceiro nunca era pago por ele. Ver
 * `deploy/data-migrations/billing-manual-payout-category/`.
 */
export const MANUAL_PAYABLE_CATEGORY_OPTIONS: ManualPayableCategoryOption[] = [
    {
        value: 'PARTNER_PAYOUT',
        label: 'Repasse a parceiro',
        recipientTypes: ['PARTNER'],
        hint: 'Entra no agrupamento de repasses do parceiro.',
    },
    {
        value: 'PROFESSIONAL_PAYOUT',
        label: 'Repasse a profissional',
        recipientTypes: ['PROFESSIONAL'],
        hint: 'Entra no agrupamento de repasses do profissional.',
    },
    {
        value: 'HOSPITAL_PAYOUT',
        label: 'Repasse a hospital',
        recipientTypes: ['HOSPITAL'],
        hint: 'Entra no agrupamento de repasses do hospital.',
    },
    {
        value: 'SUPPLIER_PAYOUT',
        label: 'Repasse a fornecedor',
        recipientTypes: ['SUPPLIER'],
        hint: 'Entra no agrupamento de repasses do fornecedor.',
    },
    {
        value: 'UTILITY',
        label: 'Despesa operacional',
        hint: 'Despesa da empresa (aluguel, energia, serviço) — não é repasse e não entra no agrupamento.',
    },
]

/** Valor do item "Personalizado" no select — não é categoria, é o modo de digitar uma. */
export const CUSTOM_CATEGORY_OPTION = '__CUSTOM__'

/**
 * As opções válidas para o destinatário escolhido. As de repasse só aparecem quando
 * o tipo de destinatário casa; as demais, sempre.
 */
export function manualPayableCategoryOptions(
    recipientType?: string | null
): ManualPayableCategoryOption[] {
    return MANUAL_PAYABLE_CATEGORY_OPTIONS.filter(
        option =>
            !option.recipientTypes ||
            (!!recipientType && option.recipientTypes.includes(recipientType))
    )
}
